import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SearchInput } from '@cbhq/cds-web/controls/SearchInput';
import { VStack } from '@cbhq/cds-web/layout';
import { Table } from '@cbhq/cds-web/tables/Table';
import { TableBody } from '@cbhq/cds-web/tables/TableBody';
import { TableCell } from '@cbhq/cds-web/tables/TableCell';
import { TableHeader } from '@cbhq/cds-web/tables/TableHeader';
import { TableRow } from '@cbhq/cds-web/tables/TableRow';
import { TextHeadline } from '@cbhq/cds-web/typography';
import { Link } from '@cbhq/cds-web/typography/Link';

import { useGetResources } from ':cloud/queries/PrivilegedAdminQueries/useGetResources';
import { ICluster } from ':cloud/types/ts_types';
import { getFormattedDateExtended, getFormattedTime } from ':cloud/utils/date';
import { Layout } from ':cloud/widgets/layout';
import { LoadingSpinner } from ':cloud/widgets/sharedcomponents';

import { RenderIfVisible } from './RenderIfVisible';

export function AdminCluster() {
  const history = useHistory();
  const { resources, isLoading: isResourcesLoading } = useGetResources();
  const [text, setText] = useState<string>('');
  const filteredList = useMemo(
    () =>
      resources.filter(
        (resource: ICluster) =>
          resource.id.includes(text) ||
          resource.name.includes(text) ||
          resource.ownerId.includes(text) ||
          resource.type.includes(text),
      ),
    [text, resources],
  );

  if (isResourcesLoading) {
    return <LoadingSpinner message="Loading all clusters..." />;
  }

  if (!resources) {
    return (
      <div>
        <TextHeadline as="p">
          There seems to be an issue gathering all customer resource information. Please check your
          network tab.
        </TextHeadline>
      </div>
    );
  }

  if (resources && resources.length === 0) {
    return (
      <div>
        <TextHeadline as="p">There are currently no resources to monitor.</TextHeadline>
      </div>
    );
  }

  const routeToResource = (resourceId: string): void => {
    history.push(`/admin/resource/${resourceId}`);
  };
  const DEFAULT_WIDTH = 320;
  const DEFAULT_WIDTH_SML = 200;

  return (
    <Layout>
      <Layout.MainContainer fullWidth>
        <VStack gap={10}>
          <SearchInput
            value={text}
            onChangeText={setText}
            placeholder="Search by Name, ResourceId (k8s namespaces), Protocol or OrgId "
          />
          <Table bordered variant="ruled">
            <TableHeader sticky>
              <RenderIfVisible>
                <TableRow backgroundColor="backgroundAlternate">
                  <TableCell width={DEFAULT_WIDTH} title="Name" />
                  <TableCell width={DEFAULT_WIDTH} title="Resource Id" />
                  <TableCell width={DEFAULT_WIDTH_SML} title="Protocol" />
                  <TableCell width={DEFAULT_WIDTH_SML} title="Workflow Version" />
                  <TableCell width={DEFAULT_WIDTH} title="Org Id" />
                  <TableCell width={DEFAULT_WIDTH_SML} title="Status" />
                  <TableCell width={DEFAULT_WIDTH_SML} title="Last Updated" />
                  <TableCell width={DEFAULT_WIDTH_SML} title="Created At" />
                </TableRow>
              </RenderIfVisible>
            </TableHeader>
            <TableBody>
              {filteredList.map(
                ({
                  id: resourceId,
                  version,
                  name,
                  ownerId: orgId,
                  type: protocolKey,
                  state: clusterState,
                  updated,
                  created,
                }: ICluster) => {
                  return (
                    <RenderIfVisible key={`row-${resourceId}`}>
                      <TableRow onChange={(): void => routeToResource(resourceId)}>
                        <TableCell width={DEFAULT_WIDTH} title={name} />
                        <TableCell width={DEFAULT_WIDTH}>
                          <Link variant="body" to={`/admin/resource/${resourceId}`}>
                            {resourceId}
                          </Link>
                        </TableCell>
                        <TableCell width={DEFAULT_WIDTH_SML} title={protocolKey} />
                        <TableCell width={DEFAULT_WIDTH_SML}>{version}</TableCell>
                        <TableCell width={DEFAULT_WIDTH} title={orgId} />
                        <TableCell width={DEFAULT_WIDTH_SML} title={clusterState} />
                        <TableCell
                          alignItems="flex-start"
                          width={DEFAULT_WIDTH_SML}
                          title={`${getFormattedDateExtended(updated)} ${getFormattedTime(
                            updated,
                          )} UTC`}
                        />
                        <TableCell
                          alignItems="flex-start"
                          width={DEFAULT_WIDTH_SML}
                          title={`${getFormattedDateExtended(created)} ${getFormattedTime(
                            created,
                          )} UTC`}
                        />
                      </TableRow>
                    </RenderIfVisible>
                  );
                },
              )}
            </TableBody>
          </Table>
        </VStack>
      </Layout.MainContainer>
    </Layout>
  );
}
